// ============================================================
// 广发圩 - Axios 客户端封装
// ============================================================

import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { storage } from '@task-bazaar/shared'

// ─── 配置 ───────────────────────────────────────────────────

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const TIMEOUT = 15_000

// ─── 创建实例 ───────────────────────────────────────────────

const instance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 登录中标志，防止登录时触发退出
let isLoggingIn = false
export function setLoggingIn(value: boolean) {
  isLoggingIn = value
}

// ─── 请求拦截器 ─────────────────────────────────────────────

instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 从 Zustand persist 存储中读取 token
    const authStorage = localStorage.getItem('auth-storage')
    let token = null
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage)
        token = parsed.state?.token
      } catch {}
    }
    // 兼容旧的存储方式
    if (!token) {
      token = storage.get<string>('token')
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ─── 响应拦截器 ─────────────────────────────────────────────

/** 清除登录状态并触发退出 */
function clearAuthAndLogout() {
  console.log('clearAuthAndLogout 被调用，调用栈:', new Error().stack)
  // 清除 zustand persist 存储
  localStorage.removeItem('auth-storage')
  // 清除旧的存储方式
  storage.remove('token')
  storage.remove('refreshToken')
  // 触发退出登录事件
  window.dispatchEvent(new CustomEvent('auth:logout'))
}

instance.interceptors.response.use(
  (response) => {
    const data = response.data

    // 业务成功
    if (data.code === 0) {
      return data
    }

    // Token 过期（排除登录中状态）
    if (data.code === 1001) {
      console.log('拦截器收到 1001 错误，isLoggingIn:', isLoggingIn)
      if (!isLoggingIn) {
        clearAuthAndLogout()
      }
      return Promise.reject(new Error(data.message || '登录已过期，请重新登录'))
    }

    // 其他业务错误
    const error = new Error(data.message || '请求失败')
    ;(error as any).code = data.code
    ;(error as any).details = data.details
    return Promise.reject(error)
  },
  (error) => {
    // 处理 HTTP 错误（4xx, 5xx）
    if (error.response) {
      const status = error.response.status
      const data = error.response.data

      if (status === 401 && !isLoggingIn) {
        clearAuthAndLogout()
      }

      // 提取服务器返回的错误信息
      const message = data?.message || data?.error || getHttpErrorMessage(status)
      const customError = new Error(message)
      ;(customError as any).status = status
      ;(customError as any).code = data?.code
      return Promise.reject(customError)
    }

    // 网络错误
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return Promise.reject(new Error('请求超时，请稍后重试'))
    }

    if (!error.response && !error.request) {
      return Promise.reject(new Error('网络异常，请检查网络连接'))
    }

    return Promise.reject(error)
  },
)

/** 获取 HTTP 错误码对应的友好提示 */
function getHttpErrorMessage(status: number): string {
  const messages: Record<number, string> = {
    400: '请求参数错误',
    403: '无权限访问',
    404: '请求的资源不存在',
    409: '操作冲突，请勿重复操作',
    422: '请求参数校验失败',
    429: '请求过于频繁，请稍后重试',
    500: '服务器内部错误',
    502: '服务器网关错误',
    503: '服务暂不可用',
    504: '服务器响应超时',
  }
  return messages[status] || `请求失败 (${status})`
}

// ─── 导出 ───────────────────────────────────────────────────

export function get<T>(url: string, config?: AxiosRequestConfig) {
  return instance.get<unknown, T>(url, config)
}

export function post<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
  return instance.post<unknown, T>(url, data, config)
}

export function put<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
  return instance.put<unknown, T>(url, data, config)
}

export function patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
  return instance.patch<unknown, T>(url, data, config)
}

export function del<T>(url: string, config?: AxiosRequestConfig) {
  return instance.delete<unknown, T>(url, config)
}

export { instance as axiosInstance }
export default instance
